import { EXTERNAL_DOMAINS } from "@shared/constants/externalDomains"

interface Config {
  ENVIRONMENT_NAME: "local" | "development" | "staging" | "production"
  API_ENDPOINT_URL: string
  WEBSOCKET_URL: string
  USES_SUBDOMAIN: boolean
  PUBLIC_HOST: string
  SECURE_COOKIE: boolean
  AUTO_CLOSE_NOTIFICATION_TIMEOUT?: number

  // Request timeout set in our `AxiosInterceptor`
  REQUEST_TIMEOUT: number

  // Upload request timeout (allow longer timeout since file uploads may take
  // time to be scanned for viruses)
  UPLOAD_TIMEOUT?: number
  RYBBIT_SITE_ID?: string
  POSTHOG_KEY?: string

  // IT-3263: Feature flag to enable the custom questionnaire builder
  // in the site page form
  CUSTOM_QUESTIONNAIRE_BUILDER: boolean

  // IT-4422: Feature flag for the care group dashboard
  CARE_GROUP_DASHBOARD: boolean

  // IT-5067: Feature flag for the two care group dashboard totals (the capacity
  // sum under the practice status table and the totals row under the
  // registrations table), until the feature has been sold to regions and insurers
  CARE_GROUP_DASHBOARD_TOTALS: boolean

  // IT-4938: Feature flag for the SBV-Z check settings (UZI-certificaat
  // section on the registrations page) until SBV-Z ships
  SBVZ_CHECK: boolean

  SERVICE_DESK_BASE_URL: string
}

/**
 * The keys of `env` that hold a boolean, so helpers that take a feature flag
 * reject a non-flag key such as `API_ENDPOINT_URL` at compile time.
 */
export type BooleanEnvKey = {
  [K in keyof Config]-?: Config[K] extends boolean ? K : never
}[keyof Config]

const local: Config = {
  ENVIRONMENT_NAME: "local",
  API_ENDPOINT_URL: import.meta.env.VITE_API_ENDPOINT_URL || "http://localhost:8000/api/v1",
  WEBSOCKET_URL: import.meta.env.VITE_WEBSOCKET_URL || "http://localhost:8000",
  USES_SUBDOMAIN: import.meta.env.VITE_SUBDOMAIN || false,
  PUBLIC_HOST: import.meta.env.VITE_PUBLIC_HOST || "localhost",

  // Close the notification banner quickly as it hovers over elements, blocking
  // Playwright from clicking on things like the "save button"
  AUTO_CLOSE_NOTIFICATION_TIMEOUT: import.meta.env.VITE_NODE_ENV === "integration-test" ? 100 : 10000,

  // IT-3583: Short request timeout local development to catch issues
  REQUEST_TIMEOUT: 2000,

  /**
   * Use shorter upload timeout during E2E tests to avoid long waits
   * @see UploadService.ts
   */
  UPLOAD_TIMEOUT: import.meta.env.VITE_NODE_ENV === "integration-test" ? 2000 : 20000,

  SECURE_COOKIE: false,

  // IT-3263: Enable the custom questionnaire builder locally
  CUSTOM_QUESTIONNAIRE_BUILDER: true,

  // IT-4422: Enable the care group dashboard locally
  CARE_GROUP_DASHBOARD: true,

  // IT-5067: Enable the care group dashboard totals locally
  CARE_GROUP_DASHBOARD_TOTALS: true,

  // IT-4938: Enable the SBV-Z check settings locally
  SBVZ_CHECK: true,

  SERVICE_DESK_BASE_URL: "https://mijnpraktijknl.atlassian.net/servicedesk/",
}

const dev: Config = {
  ENVIRONMENT_NAME: "development",
  API_ENDPOINT_URL: "https://api.dev.mijnpraktijk.nl/api/v1",
  WEBSOCKET_URL: "https://api.dev.mijnpraktijk.nl",
  USES_SUBDOMAIN: true,
  PUBLIC_HOST: "dev.mijnpraktijk.nl",
  SECURE_COOKIE: true,
  RYBBIT_SITE_ID: "",
  POSTHOG_KEY: "",

  // IT-3583: Short request timeout on dev
  REQUEST_TIMEOUT: 2500,

  // IT-3263: Enable the custom questionnaire builder on dev
  CUSTOM_QUESTIONNAIRE_BUILDER: true,

  // IT-4422: Enable the care group dashboard on dev
  CARE_GROUP_DASHBOARD: true,

  // IT-5067: Enable the care group dashboard totals on dev
  CARE_GROUP_DASHBOARD_TOTALS: true,

  // IT-4938: Enable the SBV-Z check settings on dev
  SBVZ_CHECK: true,

  SERVICE_DESK_BASE_URL: "https://mijnpraktijknl.atlassian.net/servicedesk/",
}

const staging: Config = {
  ENVIRONMENT_NAME: "staging",
  API_ENDPOINT_URL: "https://api.staging.mijnpraktijk.nl/api/v1",
  WEBSOCKET_URL: "https://api.staging.mijnpraktijk.nl",
  USES_SUBDOMAIN: true,
  PUBLIC_HOST: "staging.mijnpraktijk.nl",
  SECURE_COOKIE: true,
  RYBBIT_SITE_ID: "ba899d0e0ebf",
  POSTHOG_KEY: "phc_6EJ7uDFvDHExBRieMkXLER1Mi2hkJ9Wsg32yIOhW9Ng",

  // IT-3583: Medium request timeout on staging (should give us insight into
  // slow requests, but not hinder QA testing)
  REQUEST_TIMEOUT: 5000,

  CUSTOM_QUESTIONNAIRE_BUILDER: true,

  // IT-4422: Enable the care group dashboard on staging
  CARE_GROUP_DASHBOARD: true,

  // IT-5067: Enable the care group dashboard totals on staging
  CARE_GROUP_DASHBOARD_TOTALS: true,

  // IT-4938: Hide the SBV-Z check settings until SBV-Z ships
  SBVZ_CHECK: false,

  SERVICE_DESK_BASE_URL: "https://mijnpraktijknl.atlassian.net/servicedesk/",
}

const prod: Config = {
  ENVIRONMENT_NAME: "production",
  API_ENDPOINT_URL: "https://api.mijnpraktijk.nl/api/v1",
  WEBSOCKET_URL: "https://api.mijnpraktijk.nl",
  USES_SUBDOMAIN: true,
  PUBLIC_HOST: "mijnpraktijk.nl",
  SECURE_COOKIE: true,
  RYBBIT_SITE_ID: "",
  POSTHOG_KEY: "",

  // We use a 10s timeout for requests (slow!). It used to be 1 second, but
  // we were flooded with errors by patients trying to connect to HIS'es.
  //
  // HIS requests will take more time, so it is a trade-off. In the
  // future we may decide to set the timeout per service.
  REQUEST_TIMEOUT: 10000,

  // Feature flag for custom questionnaires (until ready)
  CUSTOM_QUESTIONNAIRE_BUILDER: true,

  // IT-4422: Hide the care group dashboard in production until it is
  // production-ready
  CARE_GROUP_DASHBOARD: true,

  // IT-5067: Hide the care group dashboard totals in production until the
  // feature has been sold to regions and insurers
  CARE_GROUP_DASHBOARD_TOTALS: false,

  // IT-4938: SBV-Z check settings (admin-only, see RegistrationSettings)
  SBVZ_CHECK: true,

  // IT-4646: Enable the invitation flow

  SERVICE_DESK_BASE_URL: "https://mijnpraktijknl.atlassian.net/servicedesk/",
}

// Matches `hostname` against `domain` on a label boundary: either an exact match or a
// `.`-delimited subdomain. A plain `endsWith` would let e.g. "notdev.mijnpraktijk.nl" be
// mistaken for a subdomain of "dev.mijnpraktijk.nl".
const matchesDomain = (hostname: string, domain: string): boolean =>
  hostname === domain || hostname.endsWith(`.${domain}`)

const matchesAnyDomain = (hostname: string, domains: string[]): boolean =>
  domains.some((domain) => matchesDomain(hostname, domain))

// A care group's landing page can also be reached directly under one of
// `EXTERNAL_DOMAINS` (e.g. `denhaag.iedereenhuisarts.nl`), so those count
// towards environment detection too — see `CareGroup.externalDomains`.
const getEnv = (): Config => {
  const { hostname } = window.location

  // We can't rely on hostname to determine environment, since using port forwarding or ngrok the hostname could be anything
  if (["localhost", "127.0.0.1"].includes(hostname) || import.meta.env.DEV) {
    return local
  } else if (
    matchesAnyDomain(hostname, ["dev.mijnpraktijk.nl", ...EXTERNAL_DOMAINS.map((domain) => `dev.${domain}`)])
  ) {
    return dev
  } else if (
    matchesAnyDomain(hostname, ["staging.mijnpraktijk.nl", ...EXTERNAL_DOMAINS.map((domain) => `staging.${domain}`)])
  ) {
    return staging
  } else if (matchesAnyDomain(hostname, ["mijnpraktijk.nl", ...EXTERNAL_DOMAINS])) {
    return prod
  } else {
    return staging
  }
}

export const env = getEnv()
